import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Plug, Upload, X } from "lucide-react";
import { api } from "@/lib/api";
import { marketingUrl } from "@/lib/appLink";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabPanel } from "@/components/Tabs";
import BillingSection from "@/pages/Billing";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const TABS = [
  { id: "preferences", label: "Preferences" },
  { id: "company", label: "Company" },
  { id: "billing", label: "Billing" },
];

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-[#16324F] focus:border-[#16324F] outline-none";
// disabled state keeps AA contrast: solid gray wash + full-contrast text, never low opacity
const lockedCls = "w-full border border-gray-200 rounded-sm px-3 py-2 text-sm bg-[#F3F4F6] text-[#374151] cursor-not-allowed disabled:opacity-100";
const btnCls = "bg-[#16324F] text-white rounded-sm px-4 py-2 text-sm hover:bg-[#10243a] transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2";
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

const ORG_KEYS = ["org_legal_name", "org_website", "org_contact_email", "org_contact_phone",
  "org_address_line", "org_city", "org_region", "org_postal_code", "org_country"];
const ORG_TOGGLES = ["org_show_contact_on_reports", "org_show_address_on_reports"];

const Field = ({ id, label, value, onChange, disabled, placeholder, mono, type = "text", testId, className = "", autoComplete }) => (
  <div className={className}>
    <label htmlFor={id} className="block text-sm font-medium mb-1.5">{label}</label>
    <input
      id={id} type={type} value={value} onChange={onChange} disabled={disabled}
      placeholder={placeholder} autoComplete={autoComplete} data-testid={testId}
      className={`${disabled ? lockedCls : inputCls} ${mono ? "font-mono" : ""}`}
    />
  </div>
);

const formFromProfile = (p) => ({
  full_name: p.full_name || "",
  pref_wcag_target: p.pref_wcag_target || "wcag22aa",
  brand_color: p.brand_color || "",
  ...Object.fromEntries(ORG_KEYS.map((k) => [k, p[k] || ""])),
  ...Object.fromEntries(ORG_TOGGLES.map((k) => [k, !!p[k]])),
});

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = TABS.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "preferences";
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [ext, setExt] = useState(null);
  const [busy, setBusy] = useState("");
  // pending navigation intercepted while the company form has unsaved edits:
  // { dest } for a link, { go } for an in-page tab switch
  const [leaveGuard, setLeaveGuard] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/me").then((r) => {
      setProfile(r.data);
      setForm(formFromProfile(r.data));
    }).catch(() => toast.error("Could not load profile"));
    api.get("/extension/status").then((r) => setExt(r.data)).catch(() => setExt({ connected: false }));
  }, []);

  const companyDirty = tab === "company" && !!profile && !!form &&
    JSON.stringify(form) !== JSON.stringify(formFromProfile(profile));

  // Hard leaves (close tab, refresh): the browser only allows its native prompt.
  useEffect(() => {
    if (!companyDirty) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [companyDirty]);

  // In-app leaves: BrowserRouter has no navigation blocker, so intercept link
  // clicks at capture phase before the router's own handler sees them.
  useEffect(() => {
    if (!companyDirty) return;
    const intercept = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a || a.target === "_blank") return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const dest = url.pathname + url.search;
      if (dest === window.location.pathname + window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      setLeaveGuard({ dest });
    };
    document.addEventListener("click", intercept, true);
    return () => document.removeEventListener("click", intercept, true);
  }, [companyDirty]);

  if (!profile || !form) return <p className="text-sm text-ink-muted font-mono">Loading...</p>;
  const isAgency = profile.plan === "agency";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setCheck = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  const patch = async (payload, doneMsg) => {
    try {
      const { data } = await api.patch("/me", payload);
      setProfile(data);
      setForm(formFromProfile(data));
      toast.success(doneMsg);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
      return false;
    }
  };

  const savePreferences = async (e) => {
    e.preventDefault();
    setBusy("prefs");
    await patch({ full_name: form.full_name, pref_wcag_target: form.pref_wcag_target }, "Preferences saved");
    setBusy("");
  };

  const toggleReportEmail = async (e) => {
    const checked = e.target.checked;
    setProfile({ ...profile, pref_email_report_ready: checked });
    try {
      const { data } = await api.patch("/me", { pref_email_report_ready: checked });
      setProfile(data);
    } catch {
      toast.error("Could not save notification preference");
      setProfile({ ...profile, pref_email_report_ready: !checked });
    }
  };

  const doSaveCompany = async () => {
    const payload = {
      ...Object.fromEntries(ORG_KEYS.map((k) => [k, form[k]])),
      ...Object.fromEntries(ORG_TOGGLES.map((k) => [k, form[k]])),
    };
    if (isAgency) payload.brand_color = form.brand_color;
    setBusy("company");
    const ok = await patch(payload, "Company profile saved");
    setBusy("");
    return ok;
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    await doSaveCompany();
  };

  // Resolution of the unsaved-changes modal: save then continue, or discard
  // the edits and continue. Either way the pending navigation is honoured.
  const resolveLeave = async (save) => {
    const guard = leaveGuard;
    setLeaveGuard(null);
    if (save) {
      const ok = await doSaveCompany();
      if (!ok) return; // save failed: stay on the page with edits intact
    } else {
      setForm(formFromProfile(profile));
    }
    if (guard?.go) guard.go();
    else if (guard?.dest) navigate(guard.dest);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy("logo");
    try {
      const { data } = await api.post("/me/logo", fd);
      setProfile(data);
      setForm(formFromProfile(data));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not upload logo");
    } finally {
      setBusy("");
    }
  };

  const removeLogo = async () => {
    setBusy("logo");
    try {
      const { data } = await api.delete("/me/logo");
      setProfile(data);
      setForm(formFromProfile(data));
      toast.success("Uploaded logo removed");
    } catch {
      toast.error("Could not remove logo");
    } finally {
      setBusy("");
    }
  };

  const previewLogo = profile.brand_logo_signed_url || "";

  const upgradeNote = !isAgency && (
    <p className="text-sm text-ink-muted mb-4" data-testid="branding-locked-note">
      Available on the Agency plan.{" "}
      <button
        type="button"
        data-testid="settings-upgrade-link"
        onClick={() => setSearchParams({ tab: "billing" })}
        className="underline text-[#16324F] rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]"
      >
        Upgrade
      </button>
    </p>
  );

  // The sticky band spans the full content width, so the reading measure is
  // constrained on the panels below it rather than on the page wrapper.
  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Settings"
        description={
          <>Signed in as <span className="font-mono">{profile.email}</span>, plan <span className="font-mono uppercase">{profile.plan}</span></>
        }
      >
        <Tabs
          tabs={TABS}
          active={tab}
          onChange={(id) => {
            const go = () => setSearchParams(id === "preferences" ? {} : { tab: id });
            if (companyDirty) setLeaveGuard({ go });
            else go();
          }}
          label="Settings sections"
          idPrefix="settings"
        />
      </PageHeader>

      <div className="max-w-4xl">
      <TabPanel id="preferences" active={tab} idPrefix="settings">
        <div className="space-y-5 max-w-lg" data-testid="settings-preferences-panel">
          <form onSubmit={savePreferences} className="bg-white border border-gray-200 rounded-sm p-5">
            <h2 className="font-heading text-base font-semibold mb-4">Your profile</h2>
            <Field id="full-name" label="Your name" value={form.full_name} onChange={set("full_name")}
              testId="settings-name-input" autoComplete="name" className="mb-4" />
            <div className="mb-4">
              <label htmlFor="profile-email" className="block text-sm font-medium mb-1.5">Email</label>
              <input id="profile-email" value={profile.email} readOnly disabled data-testid="settings-email-readonly"
                className={`${lockedCls} font-mono`} />
              <p className="text-xs text-ink-muted mt-1.5">Your email comes from your sign in account and cannot be edited here.</p>
            </div>

            {/* The acceptance is on record rather than re-asked at every sign in,
                so this is where the user can see what is on record for them. */}
            {profile.terms_accepted_at && (
              <p className="text-xs text-ink-muted mb-4" data-testid="settings-terms-accepted">
                You accepted the{" "}
                <a href={marketingUrl("/terms")} target="_blank" rel="noopener noreferrer"
                  className="text-[#16324F] underline rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]">
                  Terms of Service
                </a>{" "}
                on{" "}
                {new Date(profile.terms_accepted_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                <span className="sr-only"> (opens in a new tab)</span>.
              </p>
            )}

            <h2 className="font-heading text-base font-semibold mb-1.5 pt-2 border-t border-gray-100">Defaults</h2>
            <label htmlFor="pref-wcag" className="block text-sm font-medium mb-1.5 mt-2.5">Default WCAG target</label>
            <select id="pref-wcag" data-testid="settings-wcag-select" value={form.pref_wcag_target}
              onChange={set("pref_wcag_target")} className={`${inputCls} bg-white mb-1.5`}>
              <option value="wcag22aa">WCAG 2.2 AA</option>
              <option value="wcag21aa">WCAG 2.1 AA</option>
            </select>
            <p className="text-xs text-ink-muted mb-4">Used as the preselected target when you create a new site.</p>

            <button type="submit" data-testid="settings-save-btn" disabled={busy === "prefs"} className={btnCls}>
              {busy === "prefs" ? "Saving..." : "Save preferences"}
            </button>
          </form>

          <section className="bg-white border border-gray-200 rounded-sm p-5" aria-labelledby="ext-heading" data-testid="settings-extension-section">
            <h2 id="ext-heading" className="font-heading text-base font-semibold mb-1.5">Connect extension</h2>
            <p className="text-sm text-ink-muted mb-3">
              The browser extension files the audits you run, including staging and authenticated pages, to this workspace.
            </p>
            <p className="flex items-center gap-2 text-sm mb-4" data-testid="extension-status">
              {ext?.connected ? (
                <>
                  <CheckCircle2 size={16} strokeWidth={1.5} className="text-[#2E7D32] shrink-0" aria-hidden="true" />
                  Connected. Last paired {new Date(ext.last_connected_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}.
                </>
              ) : (
                <>
                  <Plug size={16} strokeWidth={1.5} className="text-ink-muted shrink-0" aria-hidden="true" />
                  Not connected yet.
                </>
              )}
            </p>
            <Link to="/extension/authorize" data-testid="settings-connect-extension-link"
              className={`inline-block ${btnCls}`}>
              {ext?.connected ? "Reconnect extension" : "Connect extension"}
            </Link>
          </section>

          <section className="bg-white border border-gray-200 rounded-sm p-5" aria-labelledby="notif-heading" data-testid="settings-notifications-section">
            <h2 id="notif-heading" className="font-heading text-base font-semibold mb-3">Notifications</h2>
            <label className="flex items-start gap-2.5 text-sm cursor-pointer">
              <input type="checkbox" checked={!!profile.pref_email_report_ready} onChange={toggleReportEmail}
                data-testid="settings-notify-report-checkbox"
                className="mt-0.5 h-4 w-4 accent-[#16324F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]" />
              <span>
                Email me when a report is ready
                <span className="block text-xs text-ink-muted mt-0.5">
                  Saved now, applies once report email delivery ships.
                </span>
              </span>
            </label>
          </section>
        </div>
      </TabPanel>

      <TabPanel id="company" active={tab} idPrefix="settings">
        <form onSubmit={saveCompany} className="space-y-5 max-w-2xl" data-testid="settings-company-panel">
          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <h2 className="font-heading text-base font-semibold mb-1">Organization profile</h2>
            <p className="text-sm text-ink-muted mb-4">
              These details can appear in the header of your report PDFs, so clients see who prepared the work.
              Each block below has its own display switch.
            </p>
            <Field id="org-name" label="Organization name" value={form.org_legal_name} onChange={set("org_legal_name")}
              testId="settings-org-name-input" placeholder="Andysign Studio Ltd" autoComplete="organization" className="mb-4" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <Field id="org-website" label="Website" value={form.org_website} onChange={set("org_website")}
                testId="settings-org-website-input" placeholder="https://your-agency.com" mono autoComplete="url" />
              <Field id="org-contact-email" label="Contact email" value={form.org_contact_email} onChange={set("org_contact_email")}
                testId="settings-org-email-input" placeholder="hello@your-agency.com" mono type="email" autoComplete="email" />
              <Field id="org-contact-phone" label="Contact phone" value={form.org_contact_phone} onChange={set("org_contact_phone")}
                testId="settings-org-phone-input" placeholder="+1 555 000 0000" mono type="tel" autoComplete="tel" />
            </div>
            <label className="flex items-start gap-2.5 text-sm cursor-pointer mb-4">
              <input type="checkbox" checked={form.org_show_contact_on_reports} onChange={setCheck("org_show_contact_on_reports")}
                data-testid="settings-org-show-contact-checkbox"
                className="mt-0.5 h-4 w-4 accent-[#16324F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]" />
              <span>
                Display on PDF reports
                <span className="block text-xs text-ink-muted mt-0.5">
                  Shows the organization name, website, contact email, and phone in the report header. Only filled-in fields appear.
                </span>
              </span>
            </label>

            <fieldset className="border-t border-gray-100 pt-4">
              <legend className="sr-only">Business address</legend>
              <h3 className="text-sm font-medium mb-3">Business address</h3>
              <Field id="org-address" label="Street address" value={form.org_address_line} onChange={set("org_address_line")}
                testId="settings-org-address-input" placeholder="12 High Street" autoComplete="street-address" className="mb-4" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field id="org-city" label="City" value={form.org_city} onChange={set("org_city")}
                  testId="settings-org-city-input" autoComplete="address-level2" />
                <Field id="org-region" label="Region or state" value={form.org_region} onChange={set("org_region")}
                  testId="settings-org-region-input" autoComplete="address-level1" />
                <Field id="org-postal" label="Postal code" value={form.org_postal_code} onChange={set("org_postal_code")}
                  testId="settings-org-postal-input" mono autoComplete="postal-code" />
                <Field id="org-country" label="Country" value={form.org_country} onChange={set("org_country")}
                  testId="settings-org-country-input" autoComplete="country-name" />
              </div>
              <label className="flex items-start gap-2.5 text-sm cursor-pointer mt-4">
                <input type="checkbox" checked={form.org_show_address_on_reports} onChange={setCheck("org_show_address_on_reports")}
                  data-testid="settings-org-show-address-checkbox"
                  className="mt-0.5 h-4 w-4 accent-[#16324F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]" />
                <span>
                  Display on PDF reports
                  <span className="block text-xs text-ink-muted mt-0.5">
                    Shows the business address in the report header. Only filled-in fields appear.
                  </span>
                </span>
              </label>
            </fieldset>
          </div>

          <div className="bg-white border border-gray-200 rounded-sm p-5">
            <h2 className="font-heading text-base font-semibold mb-1">Client facing branding</h2>
            <p className="text-sm text-ink-muted mb-1.5">
              Your logo and brand color appear on PDF reports and branded shareable report links.
            </p>
            {upgradeNote || <div className="mb-4" />}

            <h3 className="text-sm font-medium mb-1.5">Logo</h3>
            <div className="flex items-start gap-4 mb-2">
              <div className="w-44 h-20 shrink-0 border border-gray-200 rounded-sm bg-[#F8F9FA] flex items-center justify-center overflow-hidden" data-testid="settings-logo-preview">
                {previewLogo ? (
                  <img src={previewLogo} alt="Current logo preview" className="max-h-16 max-w-40 object-contain" />
                ) : (
                  <span className="text-xs font-mono text-ink-subtle">No logo yet</span>
                )}
              </div>
              <div className="min-w-0">
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={uploadLogo} className="sr-only" id="logo-file" data-testid="settings-logo-file-input"
                  disabled={!isAgency || busy === "logo"} />
                <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                  <button type="button" data-testid="settings-logo-upload-btn"
                    onClick={() => fileRef.current?.click()} disabled={!isAgency || busy === "logo"}
                    className={isAgency ? "inline-flex items-center gap-1.5 bg-white border border-gray-300 rounded-sm px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]" : "inline-flex items-center gap-1.5 border border-gray-200 rounded-sm px-3 py-1.5 text-sm bg-[#F3F4F6] text-[#374151] cursor-not-allowed"}>
                    <Upload size={14} strokeWidth={1.5} aria-hidden="true" />
                    {busy === "logo" ? "Working..." : "Upload logo"}
                  </button>
                  {isAgency && profile.brand_logo_path && (
                    <button type="button" data-testid="settings-logo-remove-btn" onClick={removeLogo} disabled={busy === "logo"}
                      className="inline-flex items-center gap-1 text-sm text-ink-muted underline hover:text-[#C62828] rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F]">
                      <X size={13} strokeWidth={1.5} aria-hidden="true" /> Remove uploaded logo
                    </button>
                  )}
                </div>
                <p className="text-xs text-ink-muted">
                  PNG, JPEG, WebP, or SVG, up to 1 MB. Recommended at least 240 x 80 px with a transparent background.
                </p>
              </div>
            </div>

            <label htmlFor="brand-color" className="block text-sm font-medium mb-1.5 mt-4">Brand color</label>
            <div className="flex items-center gap-2.5 mb-1">
              <input id="brand-color" type="color" data-testid="settings-color-input"
                value={HEX_RE.test(form.brand_color) ? form.brand_color : "#16324F"}
                onChange={set("brand_color")} disabled={!isAgency}
                className={`h-9 w-14 shrink-0 border border-gray-300 rounded-sm p-0.5 bg-white ${isAgency ? "cursor-pointer" : "cursor-not-allowed"}`} />
              <span className="font-mono text-sm text-ink-muted" data-testid="settings-color-value">
                {HEX_RE.test(form.brand_color) ? form.brand_color.toUpperCase() : "#16324F (default)"}
              </span>
            </div>
            <p className="text-xs text-ink-muted">
              Used as an accent on report covers and shared report pages. Body text keeps the
              standard palette so contrast holds, and a light brand color is darkened on the
              report headings it sets, far enough to stay readable and no further.
            </p>
          </div>

          <button type="submit" data-testid="settings-company-save-btn" disabled={busy === "company"} className={btnCls}>
            {busy === "company" ? "Saving..." : "Save company profile"}
          </button>
        </form>
      </TabPanel>

      <TabPanel id="billing" active={tab} idPrefix="settings">
        <div data-testid="settings-billing-panel">
          <BillingSection />
        </div>
      </TabPanel>
      </div>

      <Dialog open={!!leaveGuard} onOpenChange={(open) => { if (!open && busy !== "company") setLeaveGuard(null); }}>
        <DialogContent data-testid="settings-unsaved-modal">
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
            <DialogDescription>
              Your company profile has changes that are not saved yet. Save them before leaving, or discard them?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button data-testid="settings-unsaved-discard-btn" onClick={() => resolveLeave(false)} disabled={busy === "company"}
              className="bg-white border border-gray-300 rounded-sm px-4 py-2 text-sm hover:bg-gray-50 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2">
              Discard changes
            </button>
            <button data-testid="settings-unsaved-save-btn" onClick={() => resolveLeave(true)} disabled={busy === "company"}
              className={btnCls}>
              {busy === "company" ? "Saving..." : "Save changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
